import { type CreateSupplierInput, type Supplier, createSupplierSchema } from '#shared/types';
import { Banner } from '#client/components/ui/Banner';
import { Card } from '#client/components/ui/Card';
import { Checkbox } from '#client/components/ui/Checkbox';
import { FormActions } from '#client/components/ui/FormActions';
import { ResourceFormShell } from '#client/components/ui/ResourceFormShell';
import { TextArea } from '#client/components/ui/TextArea';
import { TextField } from '#client/components/ui/TextField';
import { useFormValues } from '#client/hooks/useFormValues';
import { useResourceForm } from '#client/hooks/useResourceForm';
import { RESOURCE } from '#client/lib/routes';
import { initialSupplierValues } from './supplier-form.logic';

const PATH = RESOURCE.suppliers;

export function SupplierFormPage() {
  return (
    <ResourceFormShell<Supplier> path={PATH} noun="supplier">
      {({ id, record }) => <SupplierForm id={id} record={record} />}
    </ResourceFormShell>
  );
}

function SupplierForm({ id, record }: { id?: string | undefined; record?: Supplier | undefined }) {
  const { values, set } = useFormValues<CreateSupplierInput>(initialSupplierValues(record));

  const form = useResourceForm({ path: PATH, id, schema: createSupplierSchema, noun: 'Supplier' });

  return (
    <form onSubmit={form.handleSubmit(values)} noValidate>
      <div className="c-form-stack">
        {form.formError && <Banner tone="critical">{form.formError}</Banner>}

        <Card>
          <div className="c-form-grid">
            <TextField
              label="Name"
              value={values.name}
              error={form.errors.name}
              onChange={(event) => set('name', event.target.value)}
              required
            />
            <TextField
              label="Code"
              value={values.code}
              error={form.errors.code}
              onChange={(event) => set('code', event.target.value)}
              helpText="Short and unique"
              required
            />
            <TextField
              label="Contact"
              value={values.contact_name ?? ''}
              error={form.errors.contact_name}
              onChange={(event) => set('contact_name', event.target.value)}
            />
            <TextField
              label="Phone"
              value={values.phone_number ?? ''}
              error={form.errors.phone_number}
              onChange={(event) => set('phone_number', event.target.value)}
            />
            <TextField
              label="Email"
              type="email"
              value={values.email ?? ''}
              error={form.errors.email}
              onChange={(event) => set('email', event.target.value)}
            />
          </div>

          <TextArea
            label="Address"
            value={values.address ?? ''}
            error={form.errors.address}
            onChange={(event) => set('address', event.target.value)}
            rows={2}
          />
          <TextArea
            label="Note"
            value={values.note ?? ''}
            error={form.errors.note}
            onChange={(event) => set('note', event.target.value)}
            rows={2}
          />

          <Checkbox
            label="Active"
            checked={values.is_active}
            onChange={(event) => set('is_active', event.target.checked)}
          />
        </Card>

        <FormActions
          cancelTo={PATH}
          submitLabel={form.isEdit ? 'Save changes' : 'Add supplier'}
          submitting={form.submitting}
        />
      </div>
    </form>
  );
}
